/**
 * S-09 · Prioridad de las garantías en el tablero (tanda EMT-CNV-2026-01).
 *
 * La regla del cliente es textual: "si el cliente está afuera siempre es la
 * prioridad". En pantalla eso tiene que verse ANTES que cualquier otra cosa, y
 * por encima de un SLA vencido: una persona en la calle no espera turno.
 *
 * El SLA en horas lo configura el dueño (Configuración → Trabajos); acá solo se
 * lee el vencimiento ya sellado en el trabajo.
 */

export type NivelPrioridadUI = 'maxima' | 'vencida';

export interface PrioridadUI {
  nivel: NivelPrioridadUI;
  /** Texto corto para el chip de la fila. */
  etiqueta: string;
  /** Por qué, en el idioma del operador. */
  motivo: string;
  /** Menor = más urgente. Sirve para ordenar. */
  orden: number;
}

/** Trabajo, tal como llega de la API, en lo que hace falta para priorizar. */
export interface JobPriorizable {
  esGarantia?: boolean;
  clienteSinAcceso?: boolean;
  slaDeadline?: string | null;
  estado?: string;
}

/** Fecha ISO → ms, o null si viene vacía o no parsea. */
function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Chip de prioridad de una garantía, o `null` cuando no hay nada que avisar.
 *
 * Devolver null en el caso normal es deliberado: si cada fila llevara un chip
 * "dentro del plazo", el chip dejaría de significar urgencia.
 */
export function prioridadGarantiaUI(
  job: JobPriorizable,
  ahora: Date = new Date(),
): PrioridadUI | null {
  if (!job.esGarantia) return null;

  if (job.clienteSinAcceso) {
    return {
      nivel: 'maxima',
      etiqueta: 'Cliente afuera',
      motivo: 'El cliente no puede entrar a su vivienda',
      orden: 0,
    };
  }

  const deadline = ms(job.slaDeadline);
  if (deadline !== null && deadline < ahora.getTime()) {
    return {
      nivel: 'vencida',
      etiqueta: 'Vencida',
      motivo: 'Pasó el tiempo comprometido para atenderla',
      orden: 1,
    };
  }
  return null;
}

/** Estados en los que un trabajo ya no está esperando atención. */
const CERRADOS = new Set(['COMPLETADO', 'NO_COMPLETADO', 'CANCELADO']);

/**
 * Parte la lista en garantías que reclaman atención YA y el resto.
 *
 * El cliente pidió ver las garantías vencidas SEPARADAS: mezcladas con el resto
 * del día se pierden entre veinte filas. Una garantía ya cerrada no entra
 * aunque su plazo haya pasado — no hay nada que hacer con ella.
 */
export function separarGarantiasVencidas<T extends JobPriorizable & { id: string }>(
  jobs: T[],
  ahora: Date = new Date(),
): { vencidas: T[]; resto: T[] } {
  const vencidas: T[] = [];
  const resto: T[] = [];

  for (const job of jobs) {
    const abierta = !CERRADOS.has(job.estado ?? '');
    const p = abierta ? prioridadGarantiaUI(job, ahora) : null;
    if (p) vencidas.push(job);
    else resto.push(job);
  }

  // Primero el cliente afuera, después las vencidas por SLA.
  vencidas.sort(
    (a, b) =>
      (prioridadGarantiaUI(a, ahora)?.orden ?? 9) - (prioridadGarantiaUI(b, ahora)?.orden ?? 9),
  );
  return { vencidas, resto };
}

// =====================================================================
// LA URGENCIA TIENE QUE LLEGARLE AL TÉCNICO
//
// Pedido del cliente en la llamada grabada. Hasta acá el "cliente afuera" y el
// "se atiende por teléfono" vivían solo en el panel: el técnico abría su lista
// y todas las tarjetas se veían iguales, así que salía a la primera de la lista
// mientras una señora esperaba en la calle.
// =====================================================================

/** Lo mínimo que hace falta de un trabajo para ordenar la lista del técnico. */
export interface JobOrdenable extends JobPriorizable {
  scheduledAt?: string | null;
}

/** Milisegundos de la cita, o +Infinity para "sin cita" (van al final). */
function citaMs(job: JobOrdenable): number {
  if (!job.scheduledAt) return Number.POSITIVE_INFINITY;
  const t = new Date(job.scheduledAt).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * La lista del técnico, ordenada por lo que de verdad importa: primero las
 * garantías que reclaman atención YA (cliente afuera, después las vencidas) y
 * recién entonces el resto por hora de cita.
 *
 * Un trabajo ya CERRADO nunca sube: no hay nada que hacer con él, y ponerlo
 * arriba sería mandarlo a una puerta donde ya estuvo.
 *
 * Devuelve una lista nueva: la original no se toca (React compara referencias).
 */
export function ordenarParaTecnico<T extends JobOrdenable>(
  jobs: T[],
  ahora: Date = new Date(),
): T[] {
  const urgencia = (job: T): number => {
    if (CERRADOS.has(job.estado ?? '')) return 9;
    return prioridadGarantiaUI(job, ahora)?.orden ?? 9;
  };
  return [...jobs].sort((a, b) => urgencia(a) - urgencia(b) || citaMs(a) - citaMs(b));
}

/** Cómo se atiende un caso: yendo al sitio o por teléfono. */
export interface TipoDeAtencion {
  /** Texto del chip de la tarjeta del técnico. */
  etiqueta: string;
  /** false = no hay a dónde ir; "Cómo llegar" no puede ser la acción principal. */
  vaAlSitio: boolean;
}

/**
 * S-04 · Un caso remoto se resuelve por teléfono: no hay dirección a la que ir.
 * Sin el dato se asume visita — es lo que era todo hasta que existió el remoto.
 */
export function tipoDeAtencion(job: { esRemoto?: boolean | null }): TipoDeAtencion {
  return job.esRemoto === true
    ? { etiqueta: 'Por teléfono', vaAlSitio: false }
    : { etiqueta: 'Visita', vaAlSitio: true };
}
